import * as React from 'react';
import type { Session, SupabaseClient } from '@supabase/supabase-js';
import { linkFailureCode, readAuthLink } from '@/lib/authLink';

/**
 * The promoter portal's own small auth layer over the shared Supabase client (design §9.3). NOT the portal
 * AuthProvider: no agency, no invite paths, no identity linking. What it keeps of the portal's rules:
 *   - PKCE: a sign-in link or an OAuth round trip comes back to `/signin?code=`, exchanged here and only here,
 *     so a link crafted for another browser cannot sign this one in (lib/authLink.ts);
 *   - implicit tokens in a fragment are stripped and never become a session;
 *   - signing out is local (other devices keep their session).
 */
export type LinkVia = 'link' | 'oauth';

export interface LinkError {
  code: string;
  error?: string;
  via: LinkVia;
}

interface PartnersAuthValue {
  client: SupabaseClient;
  session: Session | null;
  /** False until the stored session is read and a returning code is exchanged. */
  ready: boolean;
  linkError: LinkError | null;
  clearLinkError: () => void;
  signOut: () => Promise<void>;
}

const Ctx = React.createContext<PartnersAuthValue | null>(null);

/** sessionStorage key set right before an OAuth redirect: tells an OAuth return from a link return. */
export const OAUTH_MARK = 'coreway.partners.oauth';

export function markOAuthStart(provider: string): void {
  try {
    sessionStorage.setItem(OAUTH_MARK, provider);
  } catch {
    // Storage blocked: the return reads as a link, only the error wording differs.
  }
}

function takeOAuthMark(): boolean {
  try {
    const v = sessionStorage.getItem(OAUTH_MARK);
    sessionStorage.removeItem(OAUTH_MARK);
    return v !== null;
  } catch {
    return false;
  }
}

type UrlParts = { pathname: string; search: string; hash: string };

export function PartnersAuthProvider({
  client,
  url,
  replaceUrl,
  children,
}: {
  client: SupabaseClient;
  /** Where the page was loaded (default window.location); tests pass their own. */
  url?: UrlParts;
  replaceUrl?: (url: string) => void;
  children: React.ReactNode;
}) {
  const [session, setSession] = React.useState<Session | null>(null);
  const [ready, setReady] = React.useState(false);
  const [linkError, setLinkError] = React.useState<LinkError | null>(null);
  // Read once: StrictMode mounts effects twice, and a code can be exchanged only once.
  const started = React.useRef(false);

  React.useEffect(() => {
    const { data } = client.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => data.subscription.unsubscribe();
  }, [client]);

  React.useEffect(() => {
    if (started.current) return;
    started.current = true;
    const where = url ?? { pathname: window.location.pathname, search: window.location.search, hash: window.location.hash };
    const { link, cleanUrl } = readAuthLink(where);
    if (cleanUrl !== null) (replaceUrl ?? ((u: string) => window.history.replaceState(window.history.state, '', u)))(cleanUrl);
    const via: LinkVia = link.kind === 'none' ? 'link' : takeOAuthMark() ? 'oauth' : 'link';

    void (async () => {
      if (link.kind === 'code') {
        const { error } = await client.auth.exchangeCodeForSession(link.code);
        if (error) {
          console.warn('sign-in link not exchanged', error);
          setLinkError({ code: linkFailureCode(error), via });
        }
      } else if (link.kind === 'error') {
        console.warn('sign-in link error', link.description);
        setLinkError({ code: link.code, error: link.error, via });
      }
      const { data } = await client.auth.getSession();
      setSession(data.session);
      setReady(true);
    })();
  }, [client, url, replaceUrl]);

  const signOut = React.useCallback(async () => {
    await client.auth.signOut({ scope: 'local' });
  }, [client]);
  const clearLinkError = React.useCallback(() => setLinkError(null), []);

  const value = React.useMemo(
    () => ({ client, session, ready, linkError, clearLinkError, signOut }),
    [client, session, ready, linkError, clearLinkError, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePartnersAuth(): PartnersAuthValue {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('usePartnersAuth outside PartnersAuthProvider');
  return v;
}
